import { FC, useState, useEffect, useRef } from 'react'
import { Check, Loader2, Sparkles } from 'lucide-react'
import { Button } from '../../../components/ui/button'
import { useNavigate } from 'react-router-dom'
import { useLemonSqueezy } from '../../../hooks/use-lemon-squeezy'
import { useUserContext } from '../../../hooks/use-user'
import { toast } from 'sonner'

const pricingTiers = [
  {
    name: 'Basic',
    price: 9.99,
    description: 'Get started',
    features: [
      '200 inventory items',
      'Earnings dashboard',
      'Units & bins organization',
      'Active listings view'
    ],
    highlighted: false
  },
  {
    name: 'Premium',
    price: 19.99,
    description: 'Scale up',
    features: [
      '1,000 inventory items',
      'Barcode generation',
      'Full listing management',
      'Analytics & reports'
    ],
    highlighted: true
  },
  {
    name: 'Enterprise',
    price: 49.99,
    description: 'Go big',
    features: [
      'Unlimited items',
      'Team members',
      'Multi-store support',
      'Custom CSV exports'
    ],
    highlighted: false,
    comingSoon: true
  }
]

export const PricingSection: FC = () => {
  const navigate = useNavigate()
  const { createCheckoutSession, loading } = useLemonSqueezy()
  const { user } = useUserContext()
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)
  const sectionRef = useRef<HTMLDivElement>(null)
  const cardsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view')
          }
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    )

    if (sectionRef.current) observer.observe(sectionRef.current)
    if (cardsRef.current) observer.observe(cardsRef.current)

    return () => observer.disconnect()
  }, [])

  const handleSubscribe = async (tierName: string, price: number, comingSoon?: boolean) => {
    if (comingSoon) {
      toast.info('Enterprise tier coming soon!')
      return
    }

    if (!user) {
      toast.info('Create an account to get started')
      navigate('/create-account')
      return
    }

    const storeId = process.env.REACT_APP_LEMON_SQUEEZY_STORE_ID
    const variantId = tierName === 'Basic'
      ? process.env.REACT_APP_LEMON_SQUEEZY_BASIC_VARIANT_ID
      : tierName === 'Premium'
        ? process.env.REACT_APP_LEMON_SQUEEZY_PREMIUM_VARIANT_ID
        : process.env.REACT_APP_LEMON_SQUEEZY_ENTERPRISE_VARIANT_ID

    if (!storeId || !variantId) {
      toast.error('Subscription system unavailable')
      return
    }

    try {
      setCheckoutLoading(tierName)
      const checkout = await createCheckoutSession(storeId, variantId, {
        checkoutData: {
          email: user.email,
          custom: { firebase_uid: user.id }
        }
      })

      if (checkout?.checkoutUrl) {
        window.location.href = checkout.checkoutUrl
      } else {
        toast.error('Failed to create checkout')
      }
    } catch (error) {
      toast.error('Something went wrong')
    } finally {
      setCheckoutLoading(null)
    }
  }

  return (
    <section id="pricing" className="relative px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-white dark:bg-neutral800/30">
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div 
          ref={sectionRef}
          className="animate-on-scroll text-center mb-16 md:mb-20 max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral900 dark:text-neutral100 tracking-tight mb-6">
            Simple pricing.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sumoPrimary to-sumoAmber">
              Serious value.
            </span>
          </h2>
          <p className="text-lg text-neutral600 dark:text-neutral400">
            Choose your plan. Upgrade anytime.
          </p>
        </div>

        {/* Pricing cards */}
        <div 
          ref={cardsRef}
          className="stagger-children grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
        >
          {pricingTiers.map((tier, index) => (
            <div
              key={index}
              className={`relative card-hover-glow rounded-2xl p-8 border transition-all duration-300 ${
                tier.highlighted
                  ? 'bg-gradient-to-b from-sumoPrimary/5 to-transparent dark:from-sumoPrimary/10 border-sumoPrimary shadow-yellow scale-[1.02] md:scale-105'
                  : tier.comingSoon
                    ? 'bg-neutral50 dark:bg-neutral800/50 border-neutral200 dark:border-neutral700 opacity-75'
                    : 'bg-white dark:bg-neutral800 border-neutral200 dark:border-neutral700 hover:border-sumoPrimary/50'
              }`}
            >
              {/* Popular badge */}
              {tier.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-gradient-to-r from-sumoPrimary to-sumoAmber text-sumoNavy px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                  <Sparkles className="w-4 h-4" />
                  Popular
                </div>
              )}

              {/* Coming soon badge */}
              {tier.comingSoon && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-neutral500 text-white px-4 py-1.5 rounded-full text-sm font-semibold">
                  Coming Soon
                </div>
              )}

              {/* Tier header */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-neutral900 dark:text-neutral100 mb-1">
                  {tier.name}
                </h3>
                <p className="text-neutral500 dark:text-neutral400 text-sm">
                  {tier.description}
                </p>
              </div>

              {/* Price */}
              <div className="flex items-baseline mb-8">
                <span className="text-5xl font-bold text-neutral900 dark:text-neutral100">
                  ${tier.price}
                </span>
                <span className="text-neutral500 dark:text-neutral400 ml-2">/mo</span>
              </div>

              {/* Features - scannable list */}
              <ul className="space-y-4 mb-8">
                {tier.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-center gap-3">
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                      tier.highlighted 
                        ? 'bg-sumoPrimary' 
                        : 'bg-success500/20 dark:bg-success500/30'
                    }`}>
                      <Check className={`w-3 h-3 ${
                        tier.highlighted ? 'text-sumoNavy' : 'text-success700 dark:text-success500'
                      }`} />
                    </div>
                    <span className="text-neutral700 dark:text-neutral300 text-sm">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA - single clear action */}
              <Button
                variant={tier.highlighted ? 'primary' : 'outline'}
                className={`w-full py-6 rounded-xl text-base font-medium transition-all duration-200 ${
                  tier.highlighted 
                    ? 'btn-hover-lift shadow-yellow hover:shadow-strong' 
                    : 'hover:border-sumoPrimary hover:text-sumoPrimary'
                }`}
                onClick={() => handleSubscribe(tier.name, tier.price, tier.comingSoon)}
                disabled={loading || checkoutLoading === tier.name || tier.comingSoon}
              >
                {checkoutLoading === tier.name ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : tier.comingSoon ? (
                  'Notify Me'
                ) : (
                  'Get Started'
                )}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
